import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Button, 
  TextField,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Grid,
  Snackbar,
  Alert,
  Drawer,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import { Delete, Edit, Add, CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { api, getInvoiceAuditLogs } from '../services/api';
import { useUser } from '../hooks/useUser';

const TodoComponent = () => {
  const { user } = useUser();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [completed, setCompleted] = useState(false);
  const [dueDate, setDueDate] = useState(null);

  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [selectedAuditTodo, setSelectedAuditTodo] = useState(null);

  useEffect(() => {
    if (user) {
      fetchTodos();
    }
  }, [user]);

  const fetchTodos = async () => {
    try {
      setLoading(true);
      const response = await api.getTodos();
      setTodos(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching todos:', err);
      setError('Failed to fetch todos. Please try again later.');
      showSnackbar('Failed to fetch todos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (todo = null) => {
    if (todo) {
      // Edit mode
      setEditingTodo(todo);
      setTitle(todo.title);
      setDescription(todo.description || '');
      setCompleted(todo.completed);
      setDueDate(todo.due_date ? dayjs(todo.due_date) : null);
    } else {
      // Create mode
      setEditingTodo(null);
      setTitle('');
      setDescription('');
      setCompleted(false);
      setDueDate(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSubmit = async () => {
    try {
      if (!title.trim()) {
        showSnackbar('Title is required', 'error');
        return;
      }

      const todoData = {
        title,
        description: description || null,
        completed,
        due_date: dueDate ? dueDate.toISOString() : null
      };

      if (editingTodo) {
        // Update existing todo
        await api.updateTodo(editingTodo.id, todoData);
        showSnackbar('Todo updated successfully', 'success');
      } else {
        // Create new todo
        await api.createTodo(todoData);
        showSnackbar('Todo created successfully', 'success');
      }

      // Refresh todos list
      fetchTodos();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving todo:', err);
      showSnackbar('Failed to save todo', 'error');
    }
  };

  const handleToggleComplete = async (todo) => {
    try {
      await api.updateTodo(todo.id, {
        completed: !todo.completed
      });
      fetchTodos();
      showSnackbar(`Todo marked as ${!todo.completed ? 'completed' : 'incomplete'}`, 'success');
    } catch (err) {
      console.error('Error updating todo status:', err);
      showSnackbar('Failed to update todo status', 'error');
    }
  };

  const handleDelete = async (todoId) => {
    if (!window.confirm('Are you sure you want to delete this todo?')) {
      return;
    }
    
    try {
      await api.deleteTodo(todoId);
      fetchTodos();
      showSnackbar('Todo deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting todo:', err);
      showSnackbar('Failed to delete todo', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleOpenAuditDrawer = async (todo) => {
    setSelectedAuditTodo(todo);
    setAuditDrawerOpen(true);
    setAuditLoading(true);
    try {
      const res = await getInvoiceAuditLogs(todo.supplier_name, todo.month_year);
      setAuditLogs(res.data);
    } catch (err) {
      setAuditLogs([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleCloseAuditDrawer = () => {
    setAuditDrawerOpen(false);
    setAuditLogs([]);
    setSelectedAuditTodo(null);
  };

  if (loading && todos.length === 0) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4, textAlign: 'center' }}>
          <Typography variant="h6">Loading todos...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            My Todos
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Todo
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {todos.length === 0 && !loading ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1">
              You don't have any todos yet. Click "Add Todo" to create one!
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={2}>
            <List>
              {todos.map((todo, index) => (
                <React.Fragment key={todo.id}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <IconButton edge="start" onClick={() => handleToggleComplete(todo)}>
                      {todo.completed ? <CheckCircle color="success" /> : <RadioButtonUnchecked />}
                    </IconButton>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          style={{
                            textDecoration: todo.completed ? 'line-through' : 'none',
                            color: todo.completed ? 'text.secondary' : 'text.primary'
                          }}
                        >
                          {todo.title}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          {todo.description && (
                            <Typography variant="body2" color="text.secondary">
                              {todo.description}
                            </Typography>
                          )}
                          <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                            {todo.due_date && (
                              <Chip
                                size="small"
                                label={`Due: ${new Date(todo.due_date).toLocaleDateString()}`}
                                color={new Date(todo.due_date) < new Date() && !todo.completed ? "error" : "default"}
                              />
                            )}
                            {todo.domain && (
                              <Chip size="small" label={todo.domain} />
                            )}
                            {todo.amount > 0 && (
                              <Chip 
                                size="small" 
                                label={`Amount: $${todo.amount.toFixed(2)}`} 
                                color="primary" 
                              />
                            )}
                            {todo.deduction > 0 && (
                              <Chip 
                                size="small" 
                                label={`Deduction: $${todo.deduction.toFixed(2)}`} 
                                color="warning" 
                              />
                            )}
                            {todo.rejection_reason && (
                              <Chip 
                                size="small" 
                                label="Rejected" 
                                color="error"
                                title={todo.rejection_reason}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleOpenDialog(todo)} sx={{ mr: 1 }}>
                        <Edit />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleDelete(todo.id)}>
                        <Delete />
                      </IconButton>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenAuditDrawer(todo)}
                        sx={{ ml: 1 }}
                      >
                        Audit Log
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}

        {/* Todo Form Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
          <DialogTitle>{editingTodo ? 'Edit Todo' : 'Add New Todo'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="Title"
                  fullWidth
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={completed}
                      onChange={(e) => setCompleted(e.target.checked)}
                    />
                  }
                  label="Completed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="Due Date"
                      value={dueDate}
                      onChange={setDueDate}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              {editingTodo ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar 
          open={openSnackbar} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <Drawer anchor="right" open={auditDrawerOpen} onClose={handleCloseAuditDrawer}>
          <Box sx={{ width: 400, p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Audit Log
            </Typography>
            {auditLoading ? (
              <CircularProgress />
            ) : auditLogs.length === 0 ? (
              <Typography>No audit logs found.</Typography>
            ) : (
              <Stepper orientation="vertical" nonLinear activeStep={-1}>
                {auditLogs.map((log, idx) => (
                  <Step key={log.id} completed>
                    <StepLabel>{`${log.action} by ${log.performed_by}`}</StepLabel>
                    <StepContent>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(log.performed_at).toLocaleString()}
                      </Typography>
                      {log.details && (
                        <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(log.details, null, 2)}</pre>
                      )}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            )}
            <Button onClick={handleCloseAuditDrawer} sx={{ mt: 2 }} fullWidth variant="outlined">Close</Button>
          </Box>
        </Drawer>
      </Box>
    </Container>
  );
};

export default TodoComponent; 